import { useEffect, useState } from "react"

import {
  Person,
  QuestionAnswer,
  Password,
} from '@mui/icons-material'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Skeleton,
  Alert,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import { useParams, useNavigate } from "react-router-dom"

import { handleApiError, profilesService, usersService, UserType, type AIQuestionsResponse, type ProfileWithValidationResponse, type User } from "../api"
import { AuthCodeDialog } from "../components/users/AuthCodeDialog"
import { ExportDataDialog } from "../components/users/ExportDataDialog"
import { GdprDeleteDialog } from "../components/users/GdprDeleteDialog"
import { LoginAsDialog } from "../components/users/LoginAsDialog"
import { SendGridActivity } from "../components/users/SendGridActivity"
import { UpdateUserModal } from "../components/users/UpdateUserModal"
import { UserActivityLog } from "../components/users/UserActivityLog"
import { useUser } from "../hooks/use-user"
import { canLoginAsUsers, isAdminUser, isSupportUser } from "../lib/user-utils"

const formatFieldName = (field: string): string =>
  field.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim()

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return "Invalid date"
    }
    return `${date.toISOString().split('T')[0]} ${date.toTimeString().split(' ')[0]?.slice(0, 5)}`
  } catch {
    return "Invalid date"
  }
}

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { user: currentUser } = useUser()
  const navigate = useNavigate()
  const [user, setUser] = useState<User | null>(null)
  const [profileData, setProfileData] = useState<ProfileWithValidationResponse | null>(null)
  const [aiQuestions, setAiQuestions] = useState<AIQuestionsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false)
  const [error, setError] = useState("")

  const isAdmin = isAdminUser(currentUser)

  useEffect(() => {
    let ignore = false

    const fetchUserData = async () => {
      if (!id) return

      setIsLoading(true)
      setError("")
      setUser(null)
      setProfileData(null)
      setAiQuestions(null)

      // Fetch user, profile, and AI questions in parallel
      const [userData, profileData, questionsData] = await Promise.allSettled([
        usersService.getUserById(id),
        profilesService.getUserProfile(id),
        profilesService.getQuestionsForUser(id)
      ])

      if (ignore) return

      if (userData.status === 'fulfilled') {
        setUser(userData.value)
      } else {
        setError(handleApiError(userData.reason))
        setIsLoading(false)
        return
      }

      // Profile and AI questions are optional; absence clears any prior user's data
      setProfileData(profileData.status === 'fulfilled' ? profileData.value : null)
      setAiQuestions(questionsData.status === 'fulfilled' ? questionsData.value : null)
      setIsLoading(false)
    }

    fetchUserData()

    return () => {
      ignore = true
    }
  }, [id])

  const handleGenerateQuestions = async () => {
    if (!id) return

    try {
      setIsGeneratingQuestions(true)
      setError("")

      const questionsData = await profilesService.generateQuestionsForUser(id)
      setAiQuestions(questionsData)

    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsGeneratingQuestions(false)
    }
  }

  if (!id) {
    return (
      <Box sx={{ flex: 1, p: 3 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Invalid User ID
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No user ID provided
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 3 }}>
        User Details
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Skeleton variant="text" width={200} height={32} />
                <Skeleton variant="text" width={300} height={24} sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="100%" height={20} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Skeleton variant="text" width={200} height={32} />
                <Skeleton variant="text" width={300} height={24} sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="100%" height={20} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : user ? (
        <Box sx={{ maxWidth: '1200px', display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Person />
                <Typography variant="h6">
                  User Basic Info
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">{user.email}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Role
                  </Typography>
                  <Chip
                    label={user.role.toUpperCase()}
                    color={
                      user.role === "admin"
                        ? "error"
                        : user.role === "doctor"
                        ? "primary"
                        : user.role === "support"
                        ? "secondary"
                        : "default"
                    }
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Verification Status
                  </Typography>
                  <Chip
                    label={user.isVerified ? "Verified" : "Unverified"}
                    color={user.isVerified ? "success" : "warning"}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    User Type
                  </Typography>
                  <Chip
                    label={user.userType === UserType.REGULAR ? "Regular" : "Content Only"}
                    color={user.userType === UserType.REGULAR ? "primary" : "secondary"}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Country Code
                  </Typography>
                  <Typography variant="body2">{user.countryCode}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    First Name
                  </Typography>
                  <Typography variant="body2">{user.firstName || "-"}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Last Name
                  </Typography>
                  <Typography variant="body2">{user.lastName || "-"}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Created At
                  </Typography>
                  <Typography variant="body2">{formatDate(user.createdAt)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Last Updated
                  </Typography>
                  <Typography variant="body2">{formatDate(user.updatedAt)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    User ID
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', color: 'text.secondary' }}>
                    {user.id}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Admin Actions - Admin Only */}
          { (isAdmin || isSupportUser(currentUser) || canLoginAsUsers(currentUser)) && user && (
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Password />
                  <Typography variant="h6">
                    Support Actions
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {(isAdmin || isSupportUser(currentUser)) && currentUser && (
                    <UpdateUserModal
                      user={user}
                      currentUser={currentUser}
                      onUserUpdated={async () => {
                        if (id) {
                          const updatedUser = await usersService.getUserById(id)
                          setUser(updatedUser)
                        }
                      }}
                    />
                  )}
                  {(isAdmin || isSupportUser(currentUser)) && (
                    <AuthCodeDialog user={user} />
                  )}
                  {canLoginAsUsers(currentUser) && (
                    <LoginAsDialog user={user} />
                  )}
                  {(isAdmin || isSupportUser(currentUser)) && (
                    <ExportDataDialog user={user} />
                  )}
                  {(isAdmin || isSupportUser(currentUser)) && (
                    <GdprDeleteDialog
                      user={user}
                      onDeleteSuccess={() => {
                        navigate('/orders')
                      }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {id && <UserActivityLog userId={id} />}

          {id && <SendGridActivity userId={id} />}

          {profileData ? (
            <>
              {/* Profile Validation Status */}
              {profileData.missingRequiredFields && profileData.missingRequiredFields.length > 0 && (
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Profile Validation
                    </Typography>
                    <Typography variant="body2" color="warning.dark" fontWeight="medium" gutterBottom>
                      Profile Incomplete - Missing Required Fields
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {profileData.missingRequiredFields.map((field: string, index: number) => (
                        <Chip
                          key={index}
                          label={formatFieldName(field)}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      AI questions cannot be generated until these required fields are completed.
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {profileData.missingRequiredFields && profileData.missingRequiredFields.length === 0 && (
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Profile Validation
                    </Typography>
                    <Typography variant="body2" color="success.dark" fontWeight="medium">
                      All required fields are filled
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      AI questions can be generated for this user.
                    </Typography>
                  </CardContent>
                </Card>
              )}

              <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" gutterBottom>
                        Health Profile
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={6}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Age
                          </Typography>
                          <Typography variant="body2">{profileData.profile.age || "-"}</Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Sex
                          </Typography>
                          <Typography variant="body2">{profileData.profile.sex ? profileData.profile.sex.charAt(0).toUpperCase() + profileData.profile.sex.slice(1) : "-"}</Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Weight
                          </Typography>
                          <Typography variant="body2">{profileData.profile.weight || "-"}</Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Height
                          </Typography>
                          <Typography variant="body2">{profileData.profile.height || "-"}</Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Blood Pressure
                          </Typography>
                          <Typography variant="body2">
                            {profileData.profile.bloodPressureSystolic && profileData.profile.bloodPressureDiastolic
                              ? `${profileData.profile.bloodPressureSystolic}/${profileData.profile.bloodPressureDiastolic}`
                              : "-"
                            }
                          </Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Smoking
                          </Typography>
                          <Typography variant="body2">{profileData.profile.smoking ? profileData.profile.smoking.charAt(0).toUpperCase() + profileData.profile.smoking.slice(1) : "-"}</Typography>
                        </Grid>
                      </Grid>

                      {profileData.profile.healthGoals && profileData.profile.healthGoals.length > 0 && (
                        <Grid size={12} sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Health Goals
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {profileData.profile.healthGoals.map((goal, index) => (
                              <Chip key={index} label={goal} size="small" color="primary" variant="outlined" />
                            ))}
                          </Box>
                        </Grid>
                      )}

                      {profileData.profile.healthConcerns && profileData.profile.healthConcerns.length > 0 && (
                        <Grid size={12} sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Health Concerns
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {profileData.profile.healthConcerns.map((concern, index) => (
                              <Chip key={index} label={concern} size="small" color="warning" variant="outlined" />
                            ))}
                          </Box>
                        </Grid>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" gutterBottom>
                        Medical Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Medications
                          </Typography>
                          <Typography variant="body2">{profileData.profile.medications || "-"}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Supplements
                          </Typography>
                          <Typography variant="body2">{profileData.profile.supplements || "-"}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Family History
                          </Typography>
                          <Typography variant="body2">{profileData.profile.familyHistory || "-"}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Active Symptoms
                          </Typography>
                          <Typography variant="body2">{profileData.profile.activeSymptoms || "-"}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Previous Labs
                          </Typography>
                          <Typography variant="body2">{profileData.profile.previousLabs || "-"}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Tests of Interest
                          </Typography>
                          <Typography variant="body2">{profileData.profile.testsOfInterest || "-"}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {profileData.profile.dynamicQuestions && profileData.profile.dynamicQuestions.length > 0 && (
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      User Answers to Dynamic Questions & Answers
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {profileData.profile.dynamicQuestions.map((qa, index) => (
                        <Paper key={index} sx={{ p: 2, borderLeft: 4, borderColor: 'primary.light' }}>
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Question {index + 1}
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">{qa.q}</Typography>
                          </Box>
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Answer
                            </Typography>
                            <Typography variant="body2">{qa.a}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Answered on: {formatDate(qa.answeredAt)}
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      AI Generated Questions
                    </Typography>
                    <Button
                      onClick={handleGenerateQuestions}
                      disabled={isGeneratingQuestions}
                      size="small"
                      variant="outlined"
                      startIcon={isGeneratingQuestions ? <CircularProgress size={16} /> : <QuestionAnswer />}
                    >
                      {isGeneratingQuestions ? "Generating..." : "Generate New Questions"}
                    </Button>
                  </Box>
                  {aiQuestions ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {aiQuestions.questions && aiQuestions.questions.length > 0 ? (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Session ID: <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{aiQuestions.sessionId}</Typography>
                          </Typography>
                          {aiQuestions.metadata && (
                            <Paper sx={{ p: 2, bgcolor: 'grey.50', border: 1, borderColor: 'divider' }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                AI Metadata
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption">Model: {aiQuestions.metadata.modelUsed}</Typography>
                                {aiQuestions.metadata.tokensUsed && (
                                  <Typography variant="caption">Tokens Used: {aiQuestions.metadata.tokensUsed}</Typography>
                                )}
                                {aiQuestions.metadata.processingTimeMs && (
                                  <Typography variant="caption">Processing Time: {aiQuestions.metadata.processingTimeMs}ms</Typography>
                                )}
                                {aiQuestions.metadata.responseType && (
                                  <Typography variant="caption">Response Type: {aiQuestions.metadata.responseType}</Typography>
                                )}
                                {aiQuestions.metadata.status && (
                                  <Typography variant="caption">Status: {aiQuestions.metadata.status}</Typography>
                                )}
                                {aiQuestions.metadata.promptHash && (
                                  <Typography variant="caption">
                                    Prompt Hash: <Chip label={aiQuestions.metadata.promptHash.slice(0, 16) + '...'} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 16 }} />
                                  </Typography>
                                )}
                              </Box>
                              {aiQuestions.metadata.prompt && (
                                <Accordion sx={{ mt: 2 }}>
                                  <AccordionSummary sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { margin: '8px 0' } }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                      View AI Prompt
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <Paper
                                      sx={{
                                        p: 2,
                                        bgcolor: 'success.50',
                                        border: 1,
                                        borderColor: 'success.200'
                                      }}
                                    >
                                      <Typography
                                        component="pre"
                                        sx={{
                                          whiteSpace: 'pre-wrap',
                                          overflow: 'auto',
                                          maxHeight: 200,
                                          color: 'success.800',
                                          fontFamily: 'monospace',
                                          fontSize: '0.75rem',
                                          m: 0
                                        }}
                                      >
                                        {aiQuestions.metadata.prompt}
                                      </Typography>
                                    </Paper>
                                  </AccordionDetails>
                                </Accordion>
                              )}
                            </Paper>
                          )}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {aiQuestions.questions.map((question, index) => (
                              <Paper key={question.i || index} sx={{ p: 2, borderLeft: 4, borderColor: 'success.light' }}>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                  <Chip
                                    label={`ID: ${question.i}`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={question.y === 0 ? 'Single Select' : 'Multi Select'}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Box>
                                <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                                  {question.t}
                                </Typography>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" gutterBottom>
                                    Options:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {question.o.map((option, optionIndex) => (
                                      <Chip
                                        key={optionIndex}
                                        label={option}
                                        size="small"
                                        variant="outlined"
                                      />
                                    ))}
                                  </Box>
                                </Box>
                                {question.c && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                    Icon: {question.c}
                                  </Typography>
                                )}
                              </Paper>
                            ))}
                          </Box>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No AI questions generated yet.</Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No AI questions available. Click "Generate New Questions" to create some.</Typography>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Health Profile
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No health profile data available for this user.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            User Not Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The requested user could not be found.
          </Typography>
        </Box>
        )}
    </Box>
  )
}
